import React, { useEffect, useState } from "react";
import { bool, shape, arrayOf } from "prop-types";
import { push, replace } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import { connect, useDispatch } from "react-redux";
import * as configUtils from "util/config";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { selectors as onboardingSelectors, actions as onboardingActions } from "reducers/onboarding";
import { actions as modalActions } from "reducers/modal";
import MainContainer from "pages/_components/MainContainer";
import Head from "pages/_components/Head";
import Button from "pages/_components/Button";
import I18n from "pages/_components/I18n";
import ErrorPopup from "pages/_components/ErrorPopup";
import Selector from "pages/_components/fields/formik/Selector";
import CustomText from "pages/_components/CustomText/CustomText";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import HeaderSimple from "pages/_components/HeaderSimple/HeaderSimple";
import CustomData from "pages/_components/CustomData/CustomData";
import Notification from "pages/_components/Notification";
import { isIOS } from "react-device-detect";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import Uploader from "../_components/Uploader";
import EnvironmentProxySlide from "../_components/EnvironmentProxySlide";
import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import { useHistoricData } from "../hooks/useHistoricData";

const FORM_ID = "environment.step.proxies";
const PAGE_ID = "StepProxies";
const DELETE_NOTIFICATION_SCOPE = "deleteProxy";
const ROLE = "FIRMANTE_APODERADO";

const StepProxies = ({ userRoleRequest, isMobile, proxiesBackup, proxiesRequest, fetching }) => {
    const dispatch = useDispatch();
    const { status: proxiesRequestStatus, error: proxiesRequestError } = proxiesRequest;
    const [error, setError] = useState("");

    const [signerSliderIsVisible, setSignerSliderIsVisible] = useState(false);

    const { status: userRoleRequestStatus, value: userRoleRequestValue, error: userRoleRequestError } = userRoleRequest;
    const isProxy = userRoleRequestValue?.role === "FIRMANTE_APODERADO";
    const pageIsLoading = userRoleRequestStatus === "FETCHING" || fetching;

    const [proxies, setProxies] = useState([]);
    const [helpIsVisible, setHelpIsVisible] = useState(false);

    const [thubanCodePoder, setThubanCodePoder] = useState("");

    useEffect(() => {
        const poder = configUtils.get("onboarding.thuban.code.poder", "0009");
        setThubanCodePoder(poder);
    }, []);

    useEffect(() => {
        if (proxiesBackup?.proxies?.length > 0) {
            setProxies(proxiesBackup?.proxies);
        }
    }, [proxiesBackup?.proxies]);

    useEffect(() => {
        dispatch(onboardingActions.getUserRole());
    }, [dispatch]);

    useEffect(() => {
        if (userRoleRequestStatus === "FAILURE") {
            setError(userRoleRequestError);
            dispatch(modalActions.modalShow());
        }
    }, [userRoleRequestStatus, userRoleRequestError]);

    const noBackupData = proxiesBackup?.hasProxy === undefined;
    const { getBackupDataByStepId } = useHistoricData(!noBackupData);

    useEffect(() => {
        if (proxiesRequestStatus === "SUCCESS") {
            dispatch(environmentActions.setProxiesRequest({ status: "INITIAL", value: "", error: null }));
            dispatch(push("/environment/stepBeneficiaries"));
            getBackupDataByStepId("stepBeneficiaries");
        } else if (proxiesRequestStatus === "FAILURE") {
            dispatch(push("/environment/stepError"));
        }
    }, [proxiesRequestStatus, proxiesRequestError]);

    const submitIsAllowed = ({ values }) => {
        const { documentProxy, hasProxy } = values;

        let firstFieldsAreValid = false;

        if (isProxy) {
            firstFieldsAreValid = !!documentProxy;
        } else {
            firstFieldsAreValid = hasProxy === "true" ? proxies && proxies.length > 0 : true;
        }

        return firstFieldsAreValid;
    };

    const onHeaderBack = () => {
        getBackupDataByStepId("stepShareholders");
        dispatch(replace("/environment/stepShareholders"));
    };

    const formikProps = {
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            documentProxy: proxiesBackup?.documentProxy || "",
            documentProxyName: proxiesBackup?.documentProxyName || "",
            hasProxy: proxiesBackup?.hasProxy || proxiesBackup?.proxies?.length > 0 ? "true" : "false",
        },
        validationSchema: () =>
            Yup.object().shape({
                documentProxy: Yup.string(),
                hasProxy: Yup.string(),
            }),
        onSubmit: (values) => {
            if (values) {
                const finalValues = {
                    documentProxy: values?.documentProxy,
                    documentProxyName: values?.documentProxyName,
                    hasProxy: values?.hasProxy,
                    proxies,
                };

                let saveValue;

                const processProxies = [];
                proxies.forEach((proxy) => {
                    const pr = Object.assign({}, proxy);
                    delete pr.documentProxy;
                    pr.possessionCertificateIDU = proxy.documentProxy;
                    pr.possessionCertificateIDUName = proxy.documentProxyName;
                    processProxies.push(pr);
                });

                if (isProxy) {
                    saveValue = {
                        possessionCertificateIDU: finalValues.documentProxy,
                        possessionCertificateIDUName: finalValues.documentProxyName,
                        proxies: processProxies,
                    };
                } else {
                    saveValue = {
                        proxy: finalValues.hasProxy,
                        proxies: processProxies,
                    };
                }
                dispatch(environmentActions.setProxiesBackup(saveValue));
                dispatch(environmentActions.saveProxy(saveValue));
            }
        },
    };

    const submitSignerSlider = (values) => {
        setProxies((prevSigners) => [...prevSigners, { ...values }]);
    };

    const [columns, setColumns] = useState([]);
    useEffect(() => {
        const distributeShareholders = () => {
            const newColumns = [];
            proxies.forEach((shareholder, index) => {
                const columnIndex = index % 2;
                if (!newColumns[columnIndex]) {
                    newColumns[columnIndex] = [];
                }
                newColumns[columnIndex].push(shareholder);
            });
            setColumns(newColumns);
        };
        distributeShareholders();
    }, [proxies]);

    const handleDelete = (proxi) => {
        dispatch(
            environmentActions.deleteShareholders(proxi.documentNumber, ROLE, setProxies, DELETE_NOTIFICATION_SCOPE),
        );
    };

    return (
        <>
            {isMobile ? (
                <Head
                    titleText={i18nUtils.get("legalPerson.title", "Registro")}
                    onBack={onHeaderBack}
                    hideNavbarInMobile
                    showCenterContent
                    tooltipMobile
                    onClickTooltip={() => setHelpIsVisible(true)}
                    tooltipImage={isIOS ? "images/figma/icon/help-white.svg" : "images/figma/icon/help.svg"}
                />
            ) : (
                <HeaderSimple inverse />
            )}
            <Notification scopeToShow="environment" />
            <Notification scopeToShow={DELETE_NOTIFICATION_SCOPE} />

            <MainContainer className="onboarding" showLoader={pageIsLoading}>
                <EnvironmentStepper isMobile={isMobile} page="StepProxies" />
                <div className="step">
                    {isMobile && (
                        <I18n id="environment.proxies.title" defaultValue="Declaración de Apoderados" component="h1" />
                    )}
                    {!isMobile && (
                        <Head
                            titleText={i18nUtils.get("", "Declaración de Apoderados")}
                            onBack={onHeaderBack}
                            tooltip
                            showCenterContent
                            onClickTooltip={() => setHelpIsVisible(true)}
                            tooltipImage="images/figma/icon/help.svg"
                        />
                    )}
                    <Formik {...formikProps}>
                        {(allFormik) => (
                            <Form>
                                <div className="grid-table grid-table-form">
                                    <div className="grid-table-row grid-cols-12">
                                        {isProxy && (
                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <>
                                                    <I18n
                                                        id=""
                                                        defaultValue="Adjuntá tu poder que te faculte a actuar
                                                        sobre persona jurídica"
                                                    />
                                                    <Field
                                                        component={Uploader}
                                                        idForm={FORM_ID}
                                                        name="documentProxy"
                                                        label="Adjuntá documentación"
                                                        documentCode={thubanCodePoder}
                                                        defaultDocument={{
                                                            documentId: proxiesBackup?.documentProxy,
                                                            documentName: proxiesBackup?.documentProxyName,
                                                        }}
                                                        withDocumentName
                                                    />
                                                    <I18n
                                                        id=""
                                                        defaultValue={i18nUtils.get("", "Agregar más apoderados")}
                                                    />
                                                </>
                                            </div>
                                        )}
                                        {!isProxy && (
                                            <div className="grid-table-data col-start-2 col-span-5">
                                                <I18n id="" defaultValue="¿Tenés apoderado?" />
                                                <Field
                                                    inLineControl
                                                    component={Selector}
                                                    renderAs="radio"
                                                    idForm={FORM_ID}
                                                    name="hasProxy"
                                                    options={[
                                                        {
                                                            id: "true",
                                                            label: i18nUtils.get("global.yes", "Si"),
                                                        },
                                                        {
                                                            id: "false",
                                                            label: i18nUtils.get("global.no", "No"),
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        )}
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <div
                                                className="box transparent box-border-dashed"
                                                onClick={() => setSignerSliderIsVisible(true)}>
                                                <Button
                                                    className="btn-secondary icon-medium btn-only-icon enphasize"
                                                    image="plus.svg"
                                                    block={false}
                                                />
                                                <Button className="btn-link" block={false}>
                                                    <CustomText text={i18nUtils.get("", "Agregá un apoderado")} />
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <div className="md:flex md:gap-4 md:basis-1/2">
                                                {columns.map((column) => (
                                                    <div key={column[0]?.documentNumber} className="md:w-1/2">
                                                        {column.map((proxy) => (
                                                            <div
                                                                key={proxy?.documentNumber}
                                                                className="box shadow grid-table mt-7">
                                                                <div className="grid-table-row grid-cols-2">
                                                                    <div className="grid-table-data col-start-1">
                                                                        <CustomData
                                                                            titleText={i18nUtils.get("", "CUIT")}
                                                                            titleTextType="TEXT"
                                                                            valueText={proxy?.documentNumber}
                                                                            valueTextType="TEXT"
                                                                        />

                                                                        <CustomData
                                                                            titleText={i18nUtils.get("", "Celular")}
                                                                            titleTextType="TEXT"
                                                                            valueText={`+${proxy?.phoneNumber?.countryCode}${proxy?.phoneNumber?.areaCode}${proxy?.phoneNumber?.value}`}
                                                                            valueTextType="TEXT"
                                                                        />
                                                                        <CustomData
                                                                            titleText={i18nUtils.get("", "Email")}
                                                                            titleTextType="TEXT"
                                                                            valueText={proxy?.email}
                                                                            valueTextType="TEXT"
                                                                        />
                                                                    </div>
                                                                    <div className="grid-table-data col-start-2 content-end">
                                                                        <CustomButton
                                                                            pullRight
                                                                            icon="images/figma/icon/menu-mobile.svg"
                                                                            options={[
                                                                                {
                                                                                    func: () => handleDelete(proxy),
                                                                                    text: "global.delete",
                                                                                    textType: "I18N",
                                                                                },
                                                                            ]}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    {!isMobile && (
                                        <div className="grid-table-row grid-cols-12">
                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <div className="box info ">
                                                    <CustomData
                                                        icon="information_fill.svg"
                                                        titleText="environment.step.beneficiaries.infobox"
                                                        titleTextType="I18N"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="grid-table form-actions">
                                    {isMobile && (
                                        <div className="grid-table-row grid-cols-12">
                                            <div className="grid-table-data col-span-12 gap-4">
                                                <div className="box info flex flex-grow mb-6">
                                                    <CustomData
                                                        icon="information_fill.svg"
                                                        titleText="environment.step.beneficiaries.infobox"
                                                        titleTextType="I18N"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={proxiesRequestStatus === "FETCHING"}
                                                type="submit"
                                                block
                                                disabled={!submitIsAllowed(allFormik)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>

            <EnvironmentProxySlide
                slideIsVisible={signerSliderIsVisible}
                closeSlide={() => setSignerSliderIsVisible(false)}
                onSubmit={submitSignerSlider}
                isMobile={isMobile}
                proxies={proxies}
            />

            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelpIsVisible(false)}
                isMobile={isMobile}
                page={PAGE_ID}
            />

            <ErrorPopup error={error} handleClick={() => dispatch(modalActions.modalClose())} />
        </>
    );
};

StepProxies.propTypes = {
    isMobile: bool.isRequired,
    proxiesBackup: arrayOf(shape({})).isRequired,
    proxiesRequest: arrayOf(shape({})).isRequired,
    userRoleRequest: arrayOf(shape({})).isRequired,
    fetching: bool.isRequired,
};

const mapStateToProps = (state) => ({
    userRoleRequest: onboardingSelectors.getUserRoleRequest(state),
    proxiesBackup: environmentSelectors.getProxiesBackup(state),
    proxiesRequest: environmentSelectors.getProxiesRequest(state),
    fetching: onboardingSelectors.getFetching(state),
});

export default connect(mapStateToProps)(StepProxies);
